import { Card, Descriptions, Progress, Typography } from 'antd';
import { FC } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';

import { useAppSelector } from '@/shared/hooks';

import { organizationModel } from '@/entities/organization';

import { reportTypeMessages, studyTypeMessages } from '../../config/i18n';
import { selectors as storageInfoSelectors } from '../../model';

const { Title, Text } = Typography;

const getStorageColor = ({ isFull, isWarning }: { isFull: boolean; isWarning: boolean }) => {
  if (isFull) {
    return 'var(--redrose)';
  }

  if (isWarning) {
    return 'var(--orange)';
  }

  return undefined;
};

const formatStorageUnit = (
  intl: IntlShape,
  value: number,
  unit: 'gigabyte' | 'megabyte',
): string =>
  intl.formatNumber(value, {
    style: 'unit',
    unit,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatBytes = (intl: IntlShape, bytes: bigint): string => {
  const gb = Number(bytes) / 1024 ** 3;

  if (gb >= 1) {
    return formatStorageUnit(intl, gb, 'gigabyte');
  }

  return formatStorageUnit(intl, Number(bytes) / 1024 ** 2, 'megabyte');
};

type StorageInfoProps = {
  className?: string;
};

export const StorageInfo: FC<StorageInfoProps> = (props) => {
  const { className } = props;

  const intl = useIntl();
  const { formatMessage } = intl;

  const storageSize = useAppSelector(
    organizationModel.selectors.selectOrganizationStorageSize,
  );

  const storageUsed = useAppSelector(storageInfoSelectors.selectStorageUsed);
  const storageLimit = useAppSelector(storageInfoSelectors.selectTotalStorageLimit);
  const percentage = useAppSelector(storageInfoSelectors.selectStoragePercentage);
  const isWarning = useAppSelector(storageInfoSelectors.selectIsStorageWarning);
  const isFull = useAppSelector(storageInfoSelectors.selectIsStorageFull);

  if (!storageSize) {
    return null;
  }

  const storageColor = getStorageColor({ isFull, isWarning });

  return (
    <Card
      className={className}
      style={{ height: '100%' }}
      title={
        <Title level={3} style={{ marginBottom: 0 }}>
          <FormattedMessage id="storageInfo.title" defaultMessage="Storage" />
        </Title>
      }
      bordered={false}
    >
      <div style={{ marginBottom: 16 }}>
        <Progress
          percent={Math.min(percentage, 100)}
          showInfo={false}
          status={isFull ? 'exception' : 'normal'}
          strokeColor={storageColor}
        />

        <FormattedMessage
          id="storageInfo.usedOutOfLimit"
          defaultMessage="{used} / {limit} used"
          values={{
            used: <Text style={{ color: storageColor }}>{formatBytes(intl, storageUsed)}</Text>,
            limit: formatBytes(intl, storageLimit),
          }}
        />
      </div>

      <Descriptions column={1} size="small">
        <Descriptions.Item
          label={
            <FormattedMessage id="storageInfo.total" defaultMessage="Total" />
          }
        >
          {formatBytes(intl, storageSize.Total)}
        </Descriptions.Item>

        <Descriptions.Item
          label={
            <FormattedMessage
              id="storageInfo.own"
              defaultMessage="Own (logos, stamps)"
            />
          }
        >
          {formatBytes(intl, storageSize.Own)}
        </Descriptions.Item>

        <Descriptions.Item
          label={
            <FormattedMessage
              id="storageInfo.patients"
              defaultMessage="Patients (uploaded images)"
            />
          }
        >
          {formatBytes(intl, storageSize.Patients)}
        </Descriptions.Item>
      </Descriptions>

      {storageSize.Studies.length > 0 && (
        <>
          <Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>
            <FormattedMessage
              id="storageInfo.studies"
              defaultMessage="Studies"
            />
          </Title>

          <Descriptions column={1} size="small">
            {[...storageSize.Studies]
              .sort((a, b) => Number(b.Size - a.Size))
              .map((study) => (
                <Descriptions.Item
                  key={study.Type}
                  label={formatMessage(studyTypeMessages[study.Type])}
                >
                  {formatBytes(intl, study.Size)}
                </Descriptions.Item>
              ))}
          </Descriptions>
        </>
      )}

      {storageSize.Reports.length > 0 && (
        <>
          <Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>
            <FormattedMessage
              id="storageInfo.reports"
              defaultMessage="Reports"
            />
          </Title>

          <Descriptions column={1} size="small">
            {[...storageSize.Reports]
              .sort((a, b) => Number(b.Size - a.Size))
              .map((report) => (
                <Descriptions.Item
                  key={report.Type}
                  label={formatMessage(reportTypeMessages[report.Type])}
                >
                  {formatBytes(intl, report.Size)}
                </Descriptions.Item>
              ))}
          </Descriptions>
        </>
      )}
    </Card>
  );
};
